import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, AlertTriangle, Clock, Link2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { BlogSubscribeBox } from '../components/BlogSubscribeBox';
import { BLOG_POSTS, BLOG_AUTHOR, postBySlug } from '../data/blogPosts';

const fmtDate = (d) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

function Block({ b }) {
  if (b.t === 'h2') {
    return <h2 className="text-xl md:text-2xl font-semibold text-[#0F392B] mt-10 mb-4 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{b.x}</h2>;
  }
  if (b.t === 'ul') {
    return (
      <ul className="my-5 space-y-2 pl-1">
        {b.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] text-[#2A2A26] leading-relaxed">
            <span className="mt-[9px] h-1.5 w-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (b.t === 'hr') {
    return <div className="my-8 flex items-center justify-center gap-2 text-[#D4AF37] text-xs tracking-[0.5em]">• • •</div>;
  }
  return <p className="text-[15px] text-[#2A2A26] leading-[1.85] my-4">{b.x}</p>;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = postBySlug(slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Essay not found — The New Ivy Playbook" noindex />
        <MarketingNav />
        <div className="pt-32 pb-24 max-w-2xl mx-auto px-4 text-center" data-testid="blog-post-not-found">
          <AlertTriangle className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Essay not found</h1>
          <Link to="/blog">
            <Button className="bg-[#0F392B] text-white h-11 px-6 text-sm font-semibold">Back to the Playbook <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </Link>
        </div>
      </div>
    );
  }

  const more = BLOG_POSTS.filter((p) => p.slug !== slug)
    .sort((a, b) => (a.tag === post.tag ? -1 : 0) - (b.tag === post.tag ? -1 : 0))
    .slice(0, 3);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.description,
      "datePublished": post.date,
      "dateModified": post.date,
      "author": { "@type": "Person", "name": BLOG_AUTHOR.name, "jobTitle": BLOG_AUTHOR.role, "alumniOf": "Harvard University" },
      "publisher": { "@type": "EducationalOrganization", "name": "Ivy League Consulting", "url": "https://ivyleagueconsulting.org" },
      "mainEntityOfPage": `https://ivyleagueconsulting.org/blog/${post.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "The New Ivy Playbook", "item": "https://ivyleagueconsulting.org/blog" },
        { "@type": "ListItem", "position": 2, "name": post.title, "item": `https://ivyleagueconsulting.org/blog/${post.slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <SEO title={`${post.title} — The New Ivy Playbook`} description={post.description} keywords={post.keywords} schema={schema} />
      <MarketingNav />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-[#F9F9F7] border-b border-[#E2E2E2]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/blog" className="text-xs text-muted-foreground hover:text-[#0F392B] inline-flex items-center gap-1.5" data-testid="blog-post-back">
            <ArrowLeft className="h-3 w-3" /> The New Ivy Playbook
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="py-12 md:py-16 bg-[#0F392B] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] uppercase tracking-widest font-semibold text-[#D4AF37] mb-4">{post.tag}</p>
          <h1 className="text-3xl md:text-[2.75rem] font-semibold tracking-tight leading-[1.15] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.subtitle && <p className="text-base text-white/70 italic mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{post.subtitle}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/60">
            <span className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-white/10 text-[#D4AF37] flex items-center justify-center text-[10px] font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>TA</span>
              <span className="text-white/85 font-medium">{BLOG_AUTHOR.name}</span>
            </span>
            <span>·</span>
            <span>{fmtDate(post.date)}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readMins} min read</span>
          </div>
        </div>
      </header>

      {/* Body */}
      <article className="py-12 md:py-16" data-testid="blog-post-body">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {post.blocks.map((b, i) => <Block key={i} b={b} />)}

          {/* Author box */}
          <div className="mt-12 bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-6 flex items-start gap-4" data-testid="blog-post-author-box">
            <div className="w-12 h-12 rounded-full bg-[#0F392B] text-[#D4AF37] flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>TA</div>
            <div>
              <p className="text-sm font-semibold text-[#0F392B]">{BLOG_AUTHOR.name} <span className="font-normal text-muted-foreground">— {BLOG_AUTHOR.role}</span></p>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1">{BLOG_AUTHOR.bio}</p>
            </div>
          </div>

          {/* Subscribe */}
          <div className="mt-8" data-testid="blog-post-subscribe">
            <BlogSubscribeBox source={post.slug} compact />
          </div>

          {/* Related resources */}
          {post.related?.length > 0 && (
            <div className="mt-8 border border-[#0F392B]/15 bg-white rounded-2xl p-6" data-testid="blog-post-related">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold mb-3 flex items-center gap-1.5"><Link2 className="h-3 w-3" /> Go deeper</p>
              <div className="space-y-2">
                {post.related.map((r, i) => (
                  <Link key={i} to={r.href} className="block text-sm font-medium text-[#0F392B] hover:underline" data-testid={`blog-related-link-${i}`}>
                    {r.label} →
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* More from the playbook */}
      <section className="py-14 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="blog-post-more">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-medium text-[#0F392B] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>More from the Playbook</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {more.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="block bg-white border border-[#E2E2E2] rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`blog-more-${p.slug}`}>
                <p className="text-[10px] uppercase tracking-widest font-semibold text-[#D4AF37] mb-2">{p.tag}</p>
                <p className="text-sm font-semibold text-[#0F392B] leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{p.title}</p>
                <p className="text-[11px] text-muted-foreground mt-2">{p.readMins} min read</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="blog-post-cta">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Want this thinking applied to your application?
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            Start with the free diagnostic, or book a strategy call with the team behind these essays.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/diagnostic">
              <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl" data-testid="blog-post-cta-diagnostic">
                Take the Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/consultation">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="blog-post-cta-call">
                Book a Strategy Call
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
